import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiShieldCheckLight } from 'react-icons/pi';
import ProfileImage from '../../Header/ProfileImage';
import Badge from '../../shared/TailwindComponents/Badge';
import GlassCard from '../../shared/brand/GlassCard';
import { normalise } from '../../shared/access/accessModel';
import { AvatarDirectory, EMPTY_AVATAR_DIRECTORY, avatarFor } from '../people/avatarDirectory';
import { formatDateTime } from '../../../utils/functions';
import { formatRoleNameForDisplay } from '../../../utils/roleFormatter';
import { AdminUser } from './userEditModel';

export interface UserIdentityCardProps {
  user: AdminUser;
  /** The plan name to show beside the identity, when it is already known. */
  planName?: string | null;
  /** Organization pictures - `/admin/users` sends none. */
  avatarDirectory?: AvatarDirectory;
}

/**
 * Who this page is about: photo, name, and the four facts an admin scans for
 * before doing anything else. Every other panel on the page is a decision;
 * this one is only identification, which is why it is its own pane.
 */
const UserIdentityCard: React.FC<UserIdentityCardProps> = ({
  user,
  planName,
  avatarDirectory = EMPTY_AVATAR_DIRECTORY,
}) => {
  const { t } = useTranslation();
  const identity = normalise(user);
  const name = identity.name || user.email;

  return (
    <GlassCard>
      <div className="flex items-start gap-4">
        <ProfileImage
          profilePicture={avatarFor(user, avatarDirectory)}
          email={user.email}
          initials={identity.initials}
          name={name}
          size="h-11 w-11"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-medium text-fg-strong">{name}</h2>
            <Badge color={user.is_active ? 'green' : 'gray'}>
              {user.is_active ? t('Active') : t('Inactive')}
            </Badge>
            {user.is_org_admin && <Badge color="blue">{t('Admin')}</Badge>}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
            <span className="truncate">{user.email}</span>
            <span aria-hidden className="text-hairline">·</span>
            <span>
              {user.is_org_admin
                ? t('Administrator')
                : user.role
                  ? formatRoleNameForDisplay(user.role)
                  : t('Member')}
            </span>
            <span aria-hidden className="text-hairline">·</span>
            <span>{t('Joined')} {formatDateTime(user.created_at)}</span>
            {planName && (
              <>
                <span aria-hidden className="text-hairline">·</span>
                <span className="flex items-center gap-1">
                  <PiShieldCheckLight className="h-3 w-3" />
                  {planName}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export default UserIdentityCard;
